import type { i18n as I18n, PostProcessorModule } from 'i18next';

/**
 * Which `namespace:key` rendered which text, for the translation inspector.
 *
 * An i18next post-processor sees every `t()` result together with the key it
 * resolved (`postProcessPassResolved`), after interpolation, so the text is
 * exactly what reached the screen. It is switched on only while recording:
 * the rest of the time i18next skips post-processing entirely.
 */

const PROCESSOR = 'translationKeyRecorder';

interface Resolved {
  res?: unknown;
  usedNS?: string;
  exactUsedKey?: string;
}

/** Rendered text → the keys that rendered it; `null` when not recording. */
let recorded: Map<string, Set<string>> | null = null;
/**
 * Recorded text still holding `{{placeholders}}` → a pattern for it once the
 * code fills them in itself (`t('upload').replace('{{count}}', n)`), which
 * happens after `t()` and so never reaches the recorder.
 */
let patterns: Map<string, RegExp> | null = null;
/** The options recording replaced, restored when it stops. */
let previous: {
  postProcess: I18n['options']['postProcess'] | undefined;
  postProcessPassResolved: boolean | undefined;
};

/** Below this, text inside a longer string is too likely to be a coincidence. */
const MIN_CONTAINED_LENGTH = 3;

const PLACEHOLDER = /\{\{[^}]+\}\}/g;

const escapeRegExp = (text: string) =>
  text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** `null` for text that is all placeholders, which would match anything. */
function toPattern(text: string): RegExp | null {
  if (text.replace(PLACEHOLDER, '').trim().length < MIN_CONTAINED_LENGTH) {
    return null;
  }
  const source = text.split(PLACEHOLDER).map(escapeRegExp).join('.+?');
  return new RegExp(`^${source}$`, 's');
}

const recorder: PostProcessorModule = {
  type: 'postProcessor',
  name: PROCESSOR,
  process(value, _key, options) {
    const resolved = (options as { i18nResolved?: Resolved }).i18nResolved;
    const text = typeof value === 'string' ? value.trim() : '';
    // A missing key has no `res`: what renders is the key itself.
    if (recorded && text && typeof resolved?.res === 'string') {
      const id = `${resolved.usedNS}:${resolved.exactUsedKey}`;
      const ids = recorded.get(text) ?? new Set<string>();
      ids.add(id);
      recorded.set(text, ids);
      if (patterns && text.includes('{{') && !patterns.has(text)) {
        const pattern = toPattern(text);
        if (pattern) patterns.set(text, pattern);
      }
    }
    return value;
  },
};

/**
 * Starts recording, and re-renders everything bound to i18next so the page
 * already on screen is recorded too.
 */
export function startRecordingTranslationKeys(i18n: I18n): void {
  if (recorded) return;
  i18n.use(recorder);
  previous = {
    postProcess: i18n.options.postProcess,
    postProcessPassResolved: i18n.options.postProcessPassResolved,
  };
  i18n.options.postProcess = [PROCESSOR];
  i18n.options.postProcessPassResolved = true;
  recorded = new Map();
  patterns = new Map();
  i18n.emit('languageChanged', i18n.language);
}

export function stopRecordingTranslationKeys(i18n: I18n): void {
  if (!recorded) return;
  const { postProcess, postProcessPassResolved } = previous;
  if (postProcess === undefined) delete i18n.options.postProcess;
  else i18n.options.postProcess = postProcess;
  if (postProcessPassResolved === undefined) {
    delete i18n.options.postProcessPassResolved;
  } else {
    i18n.options.postProcessPassResolved = postProcessPassResolved;
  }
  recorded = null;
  patterns = null;
}

/**
 * The keys behind an element's texts: a text that is exactly a translation,
 * else a translation whose placeholders the code filled in by hand, else the
 * longest translation inside one (`${t('label')}: ${value}`).
 */
export function findTranslationKeys(texts: readonly string[]): string[] {
  if (!recorded || !patterns) return [];
  const exact = new Set<string>();
  for (const text of texts) {
    for (const id of recorded.get(text.trim()) ?? []) exact.add(id);
  }
  if (exact.size > 0) return [...exact];

  const filled = new Set<string>();
  for (const [candidate, pattern] of patterns) {
    if (texts.some(text => pattern.test(text.trim()))) {
      for (const id of recorded.get(candidate) ?? []) filled.add(id);
    }
  }
  if (filled.size > 0) return [...filled];

  let longest = '';
  for (const candidate of recorded.keys()) {
    if (
      candidate.length >= MIN_CONTAINED_LENGTH &&
      candidate.length > longest.length &&
      texts.some(text => text.includes(candidate))
    ) {
      longest = candidate;
    }
  }
  return longest ? [...(recorded.get(longest) ?? [])] : [];
}
